/**
 * Командная карточка клиента (team-portal, Блок 2): команда работает с любым клиентом по {cid}.
 * Вкладки: Реквизиты (правит менеджер — форма с валидацией), Контактные лица, Структура,
 * Сотрудники (с карточкой сотрудника inline). Скоуп — teamClientApi(cid). У компании ещё «Экология»
 * (ecology-sites: площадки, проектные работы, отчётность — `TeamEcologyTab`) и «Учётные данные»,
 * а во вкладке «Реквизиты» — срок абонентского обслуживания по обучению (правят администратор и руководитель).
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { ArrowLeft, ClipboardList } from 'lucide-react'
import {
  teamApi,
  teamClientApi,
  teamEcologyApi,
  type ClientCard,
  type ClientLogin,
  type CompanyProfile,
  type OrderCard,
  type TrainingSubscription,
} from '@/lib/api'
import { useAuth } from '@/app/auth-context'
import { getErrorMessage } from '@/lib/errors'
import { serviceLabel, teamStatusBadge } from '@/lib/orders'
import { PAGE_SIZE, pageCount } from '@/lib/pagination'
import { Pagination } from '@/design-system/components/data/pagination'
import { ClientLoginsPanel, otherOrganizationsLabel } from './client-logins'
import { PageHead } from '@/design-system/components/layout/page-head'
import { Panel } from '@/design-system/components/layout/panel'
import { Button } from '@/design-system/components/primitives/button'
import { Badge } from '@/design-system/components/primitives/badge'
import { Field } from '@/design-system/components/primitives/field'
import { Input } from '@/design-system/components/primitives/input'
import { PasswordInput } from '@/design-system/components/primitives/password-input'
import { Alert } from '@/design-system/components/feedback/alert'
import { EmptyState } from '@/design-system/components/feedback/empty-state'
import {
  DataTable,
  DataTableBody,
  DataTableCell,
  DataTableHead,
  DataTableHeaderCell,
  DataTableRow,
} from '@/design-system/components/data/data-table'
import { CompanyProfileForm } from '@/company/company-profile-form'
import { ContactsManager } from '@/company/contacts-manager'
import { StructureManager, type WorkplaceLinkState } from '@/company/structure-manager'
import { EmployeesManager } from '@/company/employees-manager'
import { EmployeeCardLoader } from '@/company/employee-card-loader'
import { TeamEcologyTab } from '@/ecology/team-ecology-tab'
import { ClientManagerCard, ClientSupportManagerCard } from '@/team/client-manager-card'
import { canEditTrainingSubscription } from '@/team/roles'
import { EcoRequisites } from '@/ecology/eco-requisites'
import {
  TrainingSubscriptionCard,
  TrainingSubscriptionOrderBadge,
} from '@/training/training-subscription'

type Tab = 'profile' | 'contacts' | 'structure' | 'employees' | 'ecology' | 'credentials' | 'orders'

const TABS: { id: Tab; label: string }[] = [
  { id: 'profile', label: 'Реквизиты' },
  { id: 'contacts', label: 'Контактные лица' },
  { id: 'structure', label: 'Структура' },
  { id: 'employees', label: 'Сотрудники' },
  { id: 'ecology', label: 'Экология' },
  { id: 'credentials', label: 'Учётные данные' },
  { id: 'orders', label: 'История заказов' },
]

function formatDate(iso: string): string {
  const d = new Date(iso)
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' })
}

/** Блок «История заказов» клиента (team-portal 5.3): все заказы клиента со статусами и датами. */
function OrdersHistoryTab({ cid }: { cid: string }) {
  const navigate = useNavigate()
  const [items, setItems] = useState<OrderCard[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Смена клиента открывает первую страницу его истории: если вкладка переиспользуется без
  // размонтирования, номер страницы от прежнего клиента дал бы лишний запрос впустую.
  useEffect(() => {
    setPage(1)
  }, [cid])

  useEffect(() => {
    const controller = new AbortController()
    setLoading(true)
    setError(null)
    teamApi
      .clientOrders(cid, { limit: PAGE_SIZE, offset: (page - 1) * PAGE_SIZE }, controller.signal)
      .then((res) => {
        if (controller.signal.aborted) return
        // Страница «уехала» за пределы результатов — возвращаемся на первую.
        if (res.items.length === 0 && page > 1) {
          setPage(1)
          return
        }
        setItems(res.items)
        setTotal(res.total)
      })
      .catch((err) => {
        if (!controller.signal.aborted)
          setError(getErrorMessage(err, 'Не удалось загрузить заказы'))
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false)
      })
    return () => controller.abort()
  }, [cid, page])

  return (
    <Panel>
      {error ? (
        <Alert variant="danger" className="m-4">
          {error}
        </Alert>
      ) : loading ? (
        <p className="px-4 py-10 text-center text-txt-secondary">Загрузка…</p>
      ) : items.length === 0 ? (
        <EmptyState
          icon={ClipboardList}
          title="Заказов нет"
          description="У клиента пока нет заказов"
        />
      ) : (
        <DataTable>
          <DataTableHead>
            <DataTableRow>
              <DataTableHeaderCell>Услуга</DataTableHeaderCell>
              <DataTableHeaderCell>Объём</DataTableHeaderCell>
              <DataTableHeaderCell>Статус</DataTableHeaderCell>
              <DataTableHeaderCell>Дата</DataTableHeaderCell>
            </DataTableRow>
          </DataTableHead>
          <DataTableBody>
            {items.map((o) => {
              const badge = teamStatusBadge(o.dataStatus)
              return (
                <DataTableRow key={o.id} clickable onClick={() => navigate(`/team/orders/${o.id}`)}>
                  <DataTableCell className="font-medium">
                    {serviceLabel(o.service)}
                    {o.trainingSubscription ? (
                      <TrainingSubscriptionOrderBadge short className="ml-2 align-middle" />
                    ) : null}
                  </DataTableCell>
                  <DataTableCell className="text-txt-secondary">
                    {o.volumeSummary || '—'}
                  </DataTableCell>
                  <DataTableCell>
                    <Badge status={badge.role}>{badge.label}</Badge>
                  </DataTableCell>
                  <DataTableCell className="text-txt-secondary">
                    {formatDate(o.createdAt)}
                  </DataTableCell>
                </DataTableRow>
              )
            })}
          </DataTableBody>
        </DataTable>
      )}
      <Pagination page={page} totalPages={pageCount(total)} onChange={setPage} />
    </Panel>
  )
}

/**
 * Вкладка «Реквизиты»: реквизиты организации и — у клиента-компании — «Ответственный за экологию»
 * (файл заказчика 23.09.2026: контакт живёт в реквизитах предприятия, а не в разделе «Экология») и
 * «Абонентское обслуживание по обучению» (доработка ТЗ 05.10.2026: отметка в реквизитах
 * организации, срок договора задают администратор и руководитель — training-subscription-head).
 */
function ProfileTab({
  api,
  cid,
  isCompany,
  trainingSubscription,
  onTrainingSubscriptionSaved,
}: {
  api: ReturnType<typeof teamClientApi>
  cid: string
  isCompany: boolean
  /** Срок приходит с карточкой клиента; `undefined` — карточка ещё грузится или клиент — физлицо. */
  trainingSubscription: TrainingSubscription | undefined
  onTrainingSubscriptionSaved: (next: TrainingSubscription) => void
}) {
  const { context } = useAuth()
  const canEditSubscription = canEditTrainingSubscription(context?.user.role)
  const [profile, setProfile] = useState<CompanyProfile | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const controller = new AbortController()
    setLoading(true)
    setError(null)
    api
      .getProfile(controller.signal)
      .then((res) => {
        if (!controller.signal.aborted) setProfile(res)
      })
      .catch((err) => {
        if (!controller.signal.aborted)
          setError(getErrorMessage(err, 'Не удалось загрузить реквизиты'))
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false)
      })
    return () => controller.abort()
  }, [api])

  if (loading) return <p className="py-10 text-center text-txt-secondary">Загрузка…</p>
  if (error) return <Alert variant="danger">{error}</Alert>
  if (!profile) return null
  return (
    <>
      <CompanyProfileForm
        profile={profile}
        onSave={(input) => api.updateProfile(input)}
        onDaDataLookup={api.dadataLookup}
      />
      {isCompany ? <EcoResponsible cid={cid} /> : null}
      {isCompany && trainingSubscription ? (
        <TrainingSubscriptionCard
          // Свой экземпляр на клиента: «Сохранено» и ошибка не переходят к другому клиенту.
          key={cid}
          cid={cid}
          subscription={trainingSubscription}
          canEdit={canEditSubscription}
          onSaved={onTrainingSubscriptionSaved}
        />
      ) : null}
    </>
  )
}

/** Контакт ответственного за экологию во вкладке «Реквизиты» (скоуп клиента). */
function EcoResponsible({ cid }: { cid: string }) {
  const api = useMemo(() => teamEcologyApi(cid), [cid])
  return <EcoRequisites api={api} />
}

function EmployeesTab({ api, cid }: { api: ReturnType<typeof teamClientApi>; cid: string }) {
  const [selectedId, setSelectedId] = useState<string | null>(null)
  // Журнал сроков этого клиента — источник колонки «Обучение» в списке сотрудников.
  const loadTrainingJournal = useCallback(
    (signal: AbortSignal) => teamApi.training.journal({ client: cid }, signal),
    [cid],
  )
  if (selectedId) {
    return <EmployeeCardLoader api={api} id={selectedId} onBack={() => setSelectedId(null)} />
  }
  return (
    <EmployeesManager
      api={api}
      onOpenEmployee={setSelectedId}
      loadTrainingJournal={loadTrainingJournal}
    />
  )
}

/**
 * Учётные данные общего входа компании (team-portal): задать/сменить email и/или пароль уже
 * существующей компании. Раньше задавалось только при создании клиента. Смена завершает сессии
 * компании (бамп версии токена на бэкенде) — вход потребуется заново. Своего входа у организации
 * может не быть (создана с закреплённым входом специалиста — create-client-with-existing-login,
 * либо основной вход откреплён): тогда форма выдаёт новый вход, и нужны и почта, и пароль.
 */
function CredentialsTab({
  cid,
  currentEmail,
  sharedWith = 0,
  onSaved,
}: {
  cid: string
  currentEmail: string
  /** К скольким ещё организациям закреплён этот вход (multi-organization-login). */
  sharedWith?: number
  onSaved: (email: string) => void
}) {
  const [email, setEmail] = useState(currentEmail)
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [ok, setOk] = useState(false)

  const emailTrimmed = email.trim()
  const emailChanged =
    emailTrimmed !== '' && emailTrimmed.toLowerCase() !== currentEmail.toLowerCase()
  const wantsPassword = password !== ''
  const mismatch = wantsPassword && confirm !== password
  const nothingToDo = !emailChanged && !wantsPassword
  const noLogin = currentEmail === ''
  // Новый вход без пароля сервер не выдаст — не даём отправить заведомо неполную форму.
  const incomplete = noLogin && (emailTrimmed === '' || !wantsPassword)

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    setOk(false)
    if (emailTrimmed === '') {
      setError('Email входа не может быть пустым')
      return
    }
    if (mismatch) {
      setError('Пароли не совпадают')
      return
    }
    if (nothingToDo) {
      setError('Измените email или задайте новый пароль')
      return
    }
    if (incomplete) {
      setError('Для нового входа задайте и почту, и пароль')
      return
    }
    const input: { loginEmail?: string; password?: string } = {}
    if (emailChanged) input.loginEmail = emailTrimmed
    if (wantsPassword) input.password = password
    setSaving(true)
    try {
      const res = await teamApi.setClientCredentials(cid, input)
      onSaved(res.loginEmail)
      setEmail(res.loginEmail)
      setPassword('')
      setConfirm('')
      setOk(true)
    } catch (err) {
      setError(getErrorMessage(err, 'Не удалось сохранить учётные данные'))
    } finally {
      setSaving(false)
    }
  }

  return (
    <Panel className="p-5">
      <form className="max-w-md" onSubmit={handleSubmit} noValidate>
        {noLogin ? (
          <p className="mb-4 text-txt-secondary">
            У организации нет собственного входа: в неё заходят по входам из блока «Доступ к
            организации» ниже. Чтобы выдать организации собственный вход, задайте почту и пароль.
          </p>
        ) : (
          <p className="mb-4 text-txt-secondary">
            Общий вход компании в кабинет. Смена email или пароля завершит текущие сессии компании —
            войти потребуется заново.
          </p>
        )}
        {sharedWith > 0 ? (
          <Alert variant="info" className="mb-4">
            Этот вход используется и в других организациях ({otherOrganizationsLabel(sharedWith)}):
            смена его почты или пароля коснётся и их. Чтобы закрыть доступ только к этой организации,
            открепите вход ниже.
          </Alert>
        ) : null}
        <Field label="Email входа" required>
          {(p) => (
            <Input
              {...p}
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="login@company.ru"
              autoComplete="username"
            />
          )}
        </Field>
        <Field
          label={noLogin ? 'Пароль' : 'Новый пароль'}
          required={noLogin}
          hint={noLogin ? 'Минимум 8 символов' : 'Минимум 8 символов. Оставьте пустым, чтобы не менять'}
        >
          {(p) => (
            <PasswordInput
              {...p}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="new-password"
            />
          )}
        </Field>
        <Field
          label="Повторите пароль"
          // Обязательно ровно тогда, когда задан новый пароль: без него поле не участвует.
          required={wantsPassword}
          error={mismatch ? 'Пароли не совпадают' : undefined}
        >
          {(p) => (
            <PasswordInput
              {...p}
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              autoComplete="new-password"
            />
          )}
        </Field>
        {error ? (
          <Alert variant="danger" className="mt-2">
            {error}
          </Alert>
        ) : null}
        {ok ? (
          <Alert variant="info" className="mt-2">
            Учётные данные сохранены.
          </Alert>
        ) : null}
        <div className="mt-4">
          <Button type="submit" variant="primary" disabled={saving || nothingToDo || mismatch || incomplete}>
            {saving ? 'Сохранение…' : 'Сохранить'}
          </Button>
        </div>
      </form>
    </Panel>
  )
}

/**
 * Вкладка «Учётные данные»: общий вход организации и входы с доступом к ней
 * (multi-organization-login). Список входов загружается здесь: основной вход влияет на
 * предупреждение формы (вход закреплён за другими организациями), а открепление основного входа
 * меняет показанную почту.
 */
function CredentialsSection({
  cid,
  currentEmail,
  onSaved,
}: {
  cid: string
  currentEmail: string
  onSaved: (email: string) => void
}) {
  const [logins, setLogins] = useState<ClientLogin[] | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)
  // Перечитать список входов: форма выше могла выдать организации новый собственный вход.
  const [reloadKey, setReloadKey] = useState(0)

  useEffect(() => {
    const controller = new AbortController()
    setLoadError(null)
    teamApi
      .clientLogins(cid, controller.signal)
      .then((res) => {
        if (!controller.signal.aborted) setLogins(res.items)
      })
      .catch((err) => {
        if (!controller.signal.aborted) {
          setLoadError(getErrorMessage(err, 'Не удалось загрузить входы организации'))
          setLogins([])
        }
      })
    return () => controller.abort()
  }, [cid, reloadKey])

  const primary = logins?.find((l) => l.primary)

  function changeLogins(next: ClientLogin[]) {
    setLogins(next)
    // Основной вход мог смениться (открепили прежний) — карточка показывает актуальную почту.
    onSaved(next.find((l) => l.primary)?.email ?? '')
  }

  return (
    <div className="space-y-4">
      {/* key: после смены основного входа форма перечитывает почту. */}
      <CredentialsTab
        key={currentEmail}
        cid={cid}
        currentEmail={currentEmail}
        sharedWith={primary?.otherOrganizations ?? 0}
        onSaved={(email) => {
          onSaved(email)
          setReloadKey((k) => k + 1)
        }}
      />
      {loadError ? <Alert variant="danger">{loadError}</Alert> : null}
      {logins ? <ClientLoginsPanel cid={cid} logins={logins} onChange={changeLogins} /> : null}
    </div>
  )
}

/** Вкладка «Структура» клиента на стороне команды. */
function StructureTab({
  api,
  linkState,
}: {
  api: ReturnType<typeof teamClientApi>
  linkState: WorkplaceLinkState
}) {
  return <StructureManager api={api} linkState={linkState} />
}

const TAB_IDS: Tab[] = [
  'profile',
  'contacts',
  'structure',
  'employees',
  'ecology',
  'credentials',
  'orders',
]
/** Вкладки только клиента-компании. */
const COMPANY_TABS: ReadonlySet<Tab> = new Set(['ecology', 'credentials'])

export function ClientDetailPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const { cid } = useParams<{ cid: string }>()
  const api = useMemo(() => (cid ? teamClientApi(cid) : null), [cid])
  const [client, setClient] = useState<ClientCard | null>(null)
  // Вкладка живёт в URL (`?tab=`): переход на карточку рабочего места и «Назад» (centrika-docs!61)
  // возвращают на ту же вкладку «Структура», а не на дефолтную.
  const [searchParams, setSearchParams] = useSearchParams()
  const tabParam = searchParams.get('tab')
  let tab: Tab = TAB_IDS.includes(tabParam as Tab) ? (tabParam as Tab) : 'profile'
  // Вкладки «Экология» и «Учётные данные» есть только у клиента-компании: для физлица (или пока
  // клиент не загружен) ссылка `?tab=credentials` иначе показала бы пустую область — откатываем на
  // «Реквизиты».
  if (COMPANY_TABS.has(tab) && client?.kind !== 'company') tab = 'profile'
  const setTab = (next: Tab) => {
    setSearchParams(
      (prev) => {
        const p = new URLSearchParams(prev)
        p.set('tab', next)
        return p
      },
      { replace: true },
    )
  }

  useEffect(() => {
    if (!api) return
    const controller = new AbortController()
    api
      .getClient(controller.signal)
      .then((res) => {
        if (!controller.signal.aborted) setClient(res)
      })
      .catch(() => undefined)
    return () => controller.abort()
  }, [api])

  if (!api || !cid) return null

  return (
    <div>
      <div className="mb-4">
        <Button
          variant="secondary"
          size="sm"
          onClick={() => navigate('/team/clients')}
          leftIcon={<ArrowLeft className="size-4 stroke-current" aria-hidden />}
        >
          К списку клиентов
        </Button>
      </div>

      <PageHead
        title={client?.name ?? 'Клиент'}
        subtitle="Карточка клиента — реквизиты правит менеджер"
      />

      {client ? (
        // Менеджер клиента и менеджер по сопровождению — рядом (client-support-manager).
        <div className="grid gap-x-4 nw:grid-cols-2">
          <ClientManagerCard
            cid={cid}
            manager={client.manager}
            onChanged={(next) => setClient((c) => (c ? { ...c, manager: next } : c))}
          />
          <ClientSupportManagerCard
            cid={cid}
            manager={client.supportManager}
            onChanged={(next) => setClient((c) => (c ? { ...c, supportManager: next } : c))}
          />
        </div>
      ) : null}

      <div className="mb-4 flex flex-wrap gap-2">
        {TABS.filter((t) => !COMPANY_TABS.has(t.id) || client?.kind === 'company').map((t) => (
          <Button
            key={t.id}
            size="sm"
            variant={tab === t.id ? 'primary' : 'secondary'}
            onClick={() => setTab(t.id)}
          >
            {t.label}
          </Button>
        ))}
      </div>

      {tab === 'profile' ? (
        <ProfileTab
          api={api}
          cid={cid}
          isCompany={client?.kind === 'company'}
          trainingSubscription={client?.trainingSubscription}
          onTrainingSubscriptionSaved={(next) =>
            setClient((c) => (c ? { ...c, trainingSubscription: next } : c))
          }
        />
      ) : null}
      {tab === 'contacts' ? <ContactsManager api={api} /> : null}
      {tab === 'structure' ? (
        <StructureTab
          api={api}
          linkState={{ from: `${location.pathname}${location.search}`, clientId: cid }}
        />
      ) : null}
      {tab === 'employees' ? <EmployeesTab api={api} cid={cid} /> : null}
      {tab === 'ecology' && client?.kind === 'company' ? <TeamEcologyTab cid={cid} /> : null}
      {tab === 'credentials' && client?.kind === 'company' ? (
        <CredentialsSection
          cid={cid}
          currentEmail={client.loginEmail ?? ''}
          onSaved={(email) => setClient((c) => (c ? { ...c, loginEmail: email } : c))}
        />
      ) : null}
      {tab === 'orders' ? <OrdersHistoryTab cid={cid} /> : null}
    </div>
  )
}
